import { parseTimestamp } from '../../../utils/dateFormatter';

export const requestPersona = idea => idea.primaryPersona || idea.personas?.[0] || 'Unspecified';
export const requestType = idea => idea.appType === 'app' ? 'App' : idea.appType === 'gem' ? 'Gem' : 'Ideation';
export const requestTime = idea => idea.createdAt ? parseTimestamp(idea.createdAt).getTime() : NaN;
export const initialReportFilters = () => ({ type: 'all', priority: 'all', persona: 'all', statuses: [], period: 'all', start: '', end: '', search: '' });

// Calendar-day windows in the viewer's local timezone, inclusive of the final day.
export function dateBounds(filters, now = new Date()) {
  if (filters.period === 'all') return [-Infinity, Infinity];
  if (filters.period === 'custom') {
    const start = filters.start ? new Date(`${filters.start}T00:00:00`).getTime() : -Infinity;
    const end = filters.end ? new Date(`${filters.end}T23:59:59.999`).getTime() : Infinity;
    return [start, end];
  }
  const start = new Date(now); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - Number(filters.period) + 1);
  const end = new Date(now); end.setHours(23, 59, 59, 999);
  return [start.getTime(), end.getTime()];
}

export function filterReport(ideas, filters, now) {
  const [start, end] = dateBounds(filters, now);
  if (start > end || Number.isNaN(start) || Number.isNaN(end)) return [];
  const search = filters.search.trim().toLowerCase();
  return ideas.filter(idea => {
    if (filters.type !== 'all' && requestType(idea).toLowerCase() !== filters.type) return false;
    if (filters.persona !== 'all' && requestPersona(idea) !== filters.persona) return false;
    if (filters.priority && filters.priority !== 'all' && (idea.priority || 'normal') !== filters.priority) return false;
    if (filters.statuses.length && !filters.statuses.includes(idea.status)) return false;
    if (filters.period !== 'all') {
      const time = requestTime(idea);
      if (!Number.isFinite(time) || time < start || time > end) return false;
    }
    return !search || [idea.id, idea.title, idea.description, idea.requesterName, idea.requesterEmail, idea.assignedMakerEmail, requestPersona(idea), requestType(idea), idea.status].filter(Boolean).join(' ').toLowerCase().includes(search);
  });
}

export function personaReport(ideas) {
  const groups = new Map();
  ideas.forEach(idea => {
    const persona = requestPersona(idea);
    const row = groups.get(persona) || { persona, total: 0, published: 0 };
    row.total += 1; row.published += idea.status === 'published' ? 1 : 0;
    groups.set(persona, row);
  });
  return [...groups.values()].sort((a, b) => b.total - a.total || a.persona.localeCompare(b.persona));
}

export const reportFields = [["id", "Request ID"], ["title", "Request"], ["type", "Type"], ["persona", "Persona"], ["status", "Status"], ["priority", "Priority"], ["requester", "Requester"], ["requesterEmail", "Requester email"], ["assignee", "Assigned to"], ["createdAt", "Submitted"], ["description", "Description"], ["category", "Category"], ["personas", "All personas"], ["departments", "Departments"], ["appSubtype", "App subtype"], ["webUrl", "Web URL"], ["rcAppUrl", "RingCentral URL"], ["steps", "Steps"], ["gemUrl", "Gem URL"], ["existingToolId", "Existing tool ID"], ["existingToolTitle", "Existing tool name"], ["repoUrl", "Repository URL"], ["deploymentPlan", "Deployment plan"], ["technicalDetails", "Technical details"], ["mondayItemId", "Monday item ID"], ["mondayItemUrl", "Monday item URL"], ["requestedDeliveryDate", "Requested delivery date"], ["dotRefNumber", "DOT reference"], ["dotPublicUrl", "DOT public URL"], ["updatedAt", "Updated"]];
export const reportValue = (idea, key, statusLabel) => {
  const special = {
    type: requestType(idea), persona: requestPersona(idea), status: statusLabel(idea.status),
    priority: idea.priority === 'normal' || !idea.priority ? 'Medium' : idea.priority,
    requester: idea.requesterName || idea.requesterEmail || '', assignee: idea.assignedMakerEmail || 'Unassigned',
  };
  if (Object.prototype.hasOwnProperty.call(special, key)) return special[key];
  if (key === 'createdAt' || key === 'updatedAt') {
    const date = idea[key] ? parseTimestamp(idea[key]) : null;
    return date && Number.isFinite(date.getTime()) ? date.toLocaleString() : '';
  }
  const value = idea[key];
  return Array.isArray(value) ? value.join(key === 'steps' ? '\n' : ', ') : String(value ?? '');
};
export function sortReport(ideas, sort, statusLabel) {
  const value = idea => ['createdAt', 'updatedAt', 'requestedDeliveryDate'].includes(sort.key)
    ? (idea[sort.key] ? parseTimestamp(idea[sort.key]).getTime() || 0 : 0)
    : reportValue(idea, sort.key, statusLabel);
  return [...ideas].sort((a, b) => {
    const x = value(a), y = value(b);
    const result = typeof x === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
    return (sort.direction === 'asc' ? result : -result) || String(a.id).localeCompare(String(b.id));
  });
}
export const reportColumns = reportFields.map(([, label]) => label);
export const displayReportDate = idea => reportValue(idea, 'createdAt', s => s) || '—';
export const reportRows = (ideas, statusLabel) => ideas.map(idea => reportFields.map(([key]) => reportValue(idea, key, statusLabel)));
export const csvCell = value => {
  let text = String(value ?? '');
  if (/^[\s]*[=+\-@]|^[\t\r\n]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};
export const reportIndices = keys => keys === undefined ? reportFields.map((_, index) => index) : [...new Set(keys)].map(key => reportFields.findIndex(([field]) => field === key)).filter(index => index >= 0);
export const createReportCsv = (ideas, statusLabel, selectedKeys) => '\uFEFF' + [reportColumns, ...reportRows(ideas, statusLabel)].map(row => reportIndices(selectedKeys).map(index => row[index]).map(csvCell).join(',')).join('\r\n');
